import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, Clock, MapPin, Heart, ArrowLeft, Share2, Tag, Users, UserCheck, Repeat } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Event } from '../types/supabase';
import { SEO } from '../components/SEO';
import SignupModal, { SignupKind } from '../components/SignupModal';
import { fetchSignupCounts } from '../lib/eventSignups';
import { LinkifyText } from '../components/LinkifyText';
import { downloadICSFile, formatDateDisplay, getTimezoneAbbreviation } from '../utils/calendar';
import { format, parseISO } from 'date-fns';
import { describeRecurrence, getNextOccurrence } from '../lib/recurrence';
import toast from 'react-hot-toast';

const EventDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [signupKind, setSignupKind] = useState<SignupKind | null>(null);
  const [volunteerCount, setVolunteerCount] = useState(0);
  const [attendeeCount, setAttendeeCount] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (id) fetchEvent(id);
  }, [id]);

  const loadCounts = async (eventId: string) => {
    const { volunteers, attendees } = await fetchSignupCounts([eventId]);
    setVolunteerCount(volunteers[eventId] ?? 0);
    setAttendeeCount(attendees[eventId] ?? 0);
  };

  const fetchEvent = async (eventId: string) => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .eq('status', 'approved')
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        navigate('/events', { replace: true });
        return;
      }
      setEvent(data);
      await loadCounts(eventId);
    } catch (error) {
      console.error('Error fetching event:', error);
      toast.error('Failed to load event');
      navigate('/events', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: event?.title,
          text: event?.description,
          url,
        });
        return;
      } catch {
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success('Link copied to clipboard');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!event) return null;

  const repeats = describeRecurrence(event);
  const displayDate = (repeats && getNextOccurrence(event)) || event.date;
  const formattedDate = format(parseISO(displayDate), 'EEEE, MMMM d, yyyy');

  return (
    <>
      <SEO
        title={`${event.title} | Monday Night Group`}
        description={event.description}
        url={`https://mondaynightgroup.com/events/${event.id}`}
      />

      <section
        className="relative h-[45vh] flex items-center"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/2774556/pexels-photo-2774556.jpeg?auto=compress&cs=tinysrgb&w=1600)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-65" />
        <div className="container mx-auto px-4 z-10">
          <Link
            to="/events"
            className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            All Events
          </Link>
          <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight">
            {event.title}
          </h1>
          {event.category && (
            <span className="inline-flex items-center mt-3 px-3 py-1 bg-white/20 text-white text-sm rounded-full">
              <Tag className="w-3 h-3 mr-1.5" />
              {event.category}
            </span>
          )}
        </div>
      </section>

      <section className="py-16 bg-white" id="main-content">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="p-8">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0">
                      <Calendar className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{repeats ? 'Next Date' : 'Date'}</p>
                      <p className="text-gray-900 font-medium">{formattedDate}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0">
                      <Clock className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Time</p>
                      <p className="text-gray-900 font-medium">
                        {event.time} {getTimezoneAbbreviation(event.timezone)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0">
                      <MapPin className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Location</p>
                      <p className="text-gray-900 font-medium">{event.location}</p>
                    </div>
                  </div>
                </div>

                {repeats && (
                  <div className="flex items-center gap-3 mb-8 px-4 py-3 rounded-lg bg-blue-50 text-blue-900">
                    <Repeat className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <p className="font-medium">{repeats}</p>
                  </div>
                )}

                <div className="border-t border-gray-100 pt-6 mb-8">
                  <h2 className="text-lg font-semibold text-gray-900 mb-3">About this Event</h2>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap"><LinkifyText text={event.description} /></p>
                </div>

                {(event.allow_attendees || event.allow_volunteers) && (attendeeCount > 0 || volunteerCount > 0) && (
                  <div className="flex flex-wrap gap-4 mb-4 text-sm text-gray-600">
                    {event.allow_attendees && attendeeCount > 0 && (
                      <span className="inline-flex items-center">
                        <UserCheck className="w-4 h-4 mr-1 text-emerald-600" />
                        {attendeeCount} attending
                      </span>
                    )}
                    {event.allow_volunteers && volunteerCount > 0 && (
                      <span className="inline-flex items-center">
                        <Users className="w-4 h-4 mr-1 text-red-500" />
                        {volunteerCount} volunteering
                      </span>
                    )}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                  {event.allow_attendees && (
                    <button
                      onClick={() => setSignupKind('attendee')}
                      className="flex-1 flex items-center justify-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white py-3 px-6 rounded-lg font-medium transition-colors"
                    >
                      <UserCheck className="w-4 h-4" />
                      <span>Attend</span>
                    </button>
                  )}
                  {event.allow_volunteers && (
                    <button
                      onClick={() => setSignupKind('volunteer')}
                      className="flex-1 flex items-center justify-center space-x-2 bg-red-600 hover:bg-red-700 text-white py-3 px-6 rounded-lg font-medium transition-colors"
                    >
                      <Heart className="w-4 h-4" />
                      <span>Volunteer</span>
                    </button>
                  )}
                  <button
                    onClick={() => downloadICSFile(event)}
                    className="flex-1 flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-medium transition-colors"
                  >
                    <Calendar className="w-4 h-4" />
                    <span>Add to Calendar</span>
                  </button>
                  <button
                    onClick={handleShare}
                    className="flex items-center justify-center space-x-2 border border-gray-300 hover:bg-gray-50 text-gray-700 py-3 px-4 rounded-lg font-medium transition-colors"
                    title="Share event"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/events"
                className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to all events
              </Link>
            </div>
          </div>
        </div>
      </section>

      {event && signupKind && (
        <SignupModal
          isOpen
          onClose={() => setSignupKind(null)}
          eventId={event.id}
          eventTitle={event.title}
          kind={signupKind}
          onSignedUp={() => loadCounts(event.id)}
        />
      )}
    </>
  );
};

export default EventDetail;
